import React from 'react';
import { cn } from '../../lib/utils';

interface MandalaCardProps {
  mandalaNumber: number;
  totalHymns: number;
  description?: string;
  onClick: () => void;
  isSelected?: boolean;
}

// Thematic titles for each Mandala based on their primary content
const mandalaThemes: Record<number, { title: string; subtitle: string }> = {
  1: { title: 'The Gateway', subtitle: 'Agni & Divine Invocations' },
  2: { title: 'Cosmic Order', subtitle: 'Hymns of Gritsamada' },
  3: { title: 'Sacred Fire', subtitle: 'Vishvamitra\'s Wisdom' },
  4: { title: 'Divine Light', subtitle: 'Vamadeva\'s Visions' },
  5: { title: 'The Elements', subtitle: 'Atri\'s Revelations' },
  6: { title: 'Thunder & Rain', subtitle: 'Bharadvaja\'s Prayers' },
  7: { title: 'Celestial Waters', subtitle: 'Vasishtha\'s Hymns' },
  8: { title: 'The Offering', subtitle: 'Kanva Family Songs' },
  9: { title: 'Soma Pavamana', subtitle: 'The Sacred Elixir' },
  10: { title: 'The Mysteries', subtitle: 'Creation & Beyond' },
};

export const MandalaCard: React.FC<MandalaCardProps> = ({
  mandalaNumber,
  totalHymns,
  description,
  onClick,
  isSelected = false,
}) => {
  const theme = mandalaThemes[mandalaNumber] || { title: 'Ancient Wisdom', subtitle: 'Sacred Verses' };
  const formattedNumber = mandalaNumber.toString().padStart(2, '0');

  return (
    <article
      className={cn(
        // Base card styling - museum aesthetic
        "group cursor-pointer relative",
        "bg-white dark:bg-museum-950",
        "border border-museum-200 dark:border-museum-800",
        "rounded-museum",
        // Generous padding
        "p-8 lg:p-10",
        // Subtle transitions
        "transition-all duration-300 ease-museum",
        // Hover states - minimal lift and border change
        "hover:border-museum-400 dark:hover:border-museum-600",
        "hover:shadow-museum",
        "hover:-translate-y-0.5",
        // Selected state
        isSelected && [
          "ring-1 ring-museum-900 dark:ring-museum-100",
          "border-museum-900 dark:border-museum-100"
        ]
      )}
      onClick={onClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === 'Enter' && onClick()}
    >
      {/* Large Typographic Number */}
      <div className="mb-6">
        <span className={cn(
          "font-display font-light",
          "text-6xl lg:text-7xl",
          "text-museum-200 dark:text-museum-800",
          "leading-none",
          "transition-colors duration-300",
          "group-hover:text-museum-300 dark:group-hover:text-museum-700"
        )}>
          {formattedNumber}
        </span>
      </div>

      {/* Title Section */}
      <div className="space-y-2 mb-8">
        <h3 className={cn(
          "font-display font-normal",
          "text-2xl lg:text-3xl",
          "text-museum-900 dark:text-museum-100",
          "tracking-tight leading-tight"
        )}>
          {theme.title}
        </h3>
        <p className={cn(
          "font-body italic",
          "text-base",
          "text-museum-500 dark:text-museum-400"
        )}>
          {description || theme.subtitle}
        </p>
      </div>

      {/* Footer - Monospace Label */}
      <div className="flex items-center justify-between">
        <span className={cn(
          "font-mono font-medium",
          "text-[10px] uppercase tracking-[0.15em]",
          "text-museum-400 dark:text-museum-500"
        )}>
          {totalHymns} Hymns
        </span>

        {/* Subtle Arrow on Hover */}
        <div className={cn(
          "flex items-center gap-1",
          "text-museum-400 dark:text-museum-500",
          "opacity-0 translate-x-1",
          "group-hover:opacity-100 group-hover:translate-x-0",
          "transition-all duration-300"
        )}>
          <span className="font-mono text-[10px] uppercase tracking-wider">
            View
          </span>
          <svg
            className="w-3 h-3"
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
            strokeWidth={1.5}
          >
            <path
              strokeLinecap="round"
              strokeLinejoin="round"
              d="M4.5 12h15m0 0l-6.75-6.75M19.5 12l-6.75 6.75"
            />
          </svg>
        </div>
      </div>

      {/* Bottom Border Accent - reveals on hover */}
      <div className={cn(
        "absolute bottom-0 left-0 right-0 h-px",
        "bg-museum-900 dark:bg-museum-100",
        "transform scale-x-0 origin-left",
        "group-hover:scale-x-100",
        "transition-transform duration-500 ease-museum-smooth"
      )} />
    </article>
  );
};

export default MandalaCard;
